import React, { useState, useEffect } from 'react';
import { applyPwaManifest } from '../utils/pwa';
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const AdminLayout = () => {
  const { user, logout, canAccess, canAccessPath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    applyPwaManifest('admin');
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Waad ka baxday!');
    navigate('/login');
  };

  const allNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '🏠', permission: 'dashboard' },
    { path: '/admin/quotations', label: 'Quatation', icon: '📋', permission: 'quotations' },
    { path: '/admin/invoices', label: 'Invoices', icon: '📄', permission: 'invoices' },
    { path: '/admin/payments', label: 'Lacagaha', icon: '💳', permission: 'payments' },
    { path: '/admin/employees', label: 'Shaqaalaha', icon: '👥', permission: 'employees' },
    { path: '/admin/reports', label: 'Warbixinaha', icon: '📊', permission: 'reports' },
    { path: '/admin/project-costs', label: 'Qarash Mashruuc', icon: '📑', permission: 'projectCosts' },
    { path: '/admin/dalale', label: 'Dalale', icon: '🤝', permission: 'dalale' },
    { path: '/admin/apartment-registry', label: 'Diiwaanka Guryaha', icon: '🏢', permission: 'apartmentRegistry' },
    { path: '/admin/inventory', label: 'Inventory', icon: '📦', permission: 'inventory' },
    { path: '/admin/users', label: 'Users', icon: '👤', permission: 'users' },
    { path: '/admin/settings', label: 'Settings', icon: '⚙️', permission: 'settings' }
  ];

  const navItems = allNavItems.filter((item) => canAccess(item.permission));

  const mobileNav = navItems.filter((item) =>
    ['dashboard', 'quotations', 'payments', 'employees', 'inventory', 'reports'].includes(item.permission)
  );

  if (!canAccessPath(location.pathname)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Desktop Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-40 w-64 transition-transform 
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 bg-white border-r shadow-lg md:shadow-none
          flex flex-col overflow-hidden
        `}
        style={{ height: '100dvh' }}
      >
        <div className="flex items-center justify-center h-16 border-b flex-shrink-0">
          <img src="/logo.png" alt="Logo" className="h-10" />
          <span className="ml-2 text-lg font-bold text-red-600">SAHALGUUR</span>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto flex-1 min-h-0">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive ? 'bg-red-50 text-red-600 font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <span className="mr-3 text-xl">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t bg-white flex-shrink-0">
          <p className="text-sm text-gray-500 mb-2 truncate">{user?.email}</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
          >
            Ka bax
          </button>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="md:ml-64 flex flex-col flex-1">
        {/* Top Header */}
        <header className="bg-white border-b h-16 flex items-center justify-between px-4 sticky top-0 z-30">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)} 
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="md:hidden">
            <img src="/logo.png" alt="Logo" className="h-8" />
          </div>
          <div></div>
        </header>

        {/* ✅ FIXED: Scrollable Main Content with bottom padding */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-mobile-nav">
          <Outlet />
        </main>
      </div>

      {/* ✅ Mobile Bottom Navigation - Hidden when sidebar is open */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 bg-white border-t z-50 safe-area-bottom ${sidebarOpen ? 'hidden' : ''}`}>
        <div className="flex justify-around items-center h-16">
          {mobileNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center py-2 px-3 ${isActive ? 'text-red-600' : 'text-gray-500'}`
              }
            >
              <span className="text-xl mb-1">{item.icon}</span>
              <span className="text-xs">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" 
          onClick={() => setSidebarOpen(false)} 
        />
      )}
    </div>
  );
};

export default AdminLayout;